import {useLazyQuery} from "@apollo/client/react";
import {
    CtrBureaux,
    CtrFirmes,
    CtrInscriptions,
    CtrMembres,
    Maybe,
    Query,
    WebsiteListingIndexPolicy
} from "../graphql/generated/graphql-main.tsx";
import {gqlTemplates} from "../utils/gql-client.ts";
import {coreActions} from "../store/slice-core";
import {useXDispatch, useXSelector} from "../store/hooks.ts";
import {useEffect} from "react";
import {findBy} from "../utils/array-utils.ts";
import useSiteConfig from "./useSiteConfig.ts";
import {useLazyFetchEvOfficesQuery} from "../store/api-rest.ts";
import {empty} from "../utils/utils.ts";

const gk1: keyof Query = "ctrFirmes";
const gk2: keyof Query = "ctrBureaux";
const gk3: keyof Query = "ctrMembres";

export default function useCtrBrokers<
    F extends CtrFirmes,
    B extends CtrBureaux,
    M extends CtrMembres
>() {
    const dispatch = useXDispatch();
    const $firmes = useXSelector(s => s.core.FIRMES as F[]);
    const $bureaux = useXSelector(s => s.core.BUREAUX as B[]);
    const $membres = useXSelector(s => s.core.MEMBRES as M[]);
    const $ev_offices = useXSelector(s => s.core.ev_offices);

    const {$config} = useSiteConfig();

    const [fetchFirmes, {loading: loading1}] = useLazyQuery<Query>(
        gqlTemplates.firmes, {fetchPolicy: "cache-and-network"});
    const [fetchBureaux, {loading: loading2}] = useLazyQuery<Query>(
        gqlTemplates.bureaux, {fetchPolicy: "cache-and-network"});
    const [fetchMembres, {loading: loading3}] = useLazyQuery<Query>(
        gqlTemplates.membres, {fetchPolicy: "cache-and-network"});
    const [fetchEvOffices] = useLazyFetchEvOfficesQuery();

    const loadFirmes = async () => {
        const {data} = await fetchFirmes();
        if (data?.[gk1] == null) return;
        dispatch(coreActions.$setFirmes(data?.[gk1] as F[]));
    }
    const loadBureaux = async () => {
        const {data} = await fetchBureaux();
        if (data?.[gk2] == null) return;
        dispatch(coreActions.$setBureaux(data?.[gk2] as B[]));
    }
    const loadMembres = async () => {
        const {data} = await fetchMembres();
        if (data?.[gk3] == null) return;
        dispatch(coreActions.$setMembres(data?.[gk3] as M[]));
    }
    const loadEvOffices = async () => {
        const {data} = await fetchEvOffices();
        console.info(`ev-offices.data (rtk)`, data);
        dispatch(coreActions.$setEvOffices(data ?? {}));
    }

    const membreByCode = (code?: Maybe<string>) => {
        return findBy($membres, "CODE", code);
    }
    const bureauByCode = (code?: Maybe<string>) => {
        return findBy($bureaux, "CODE", code);
    }
    const firmeByBureau = (code?: Maybe<string>) => {
        const _office = bureauByCode(code);
        if (_office == null) return null;
        return findBy($firmes, "CODE", _office?.FIRME_CODE);
    }

    // get title-case agency name given a broker ID (used to display where broker is coming from)
    const shortAgencyName = (
        broker?: Maybe<string>, restricted?: boolean
    ) => {
        const _broker = membreByCode(broker);
        const _firme = firmeByBureau(_broker?.BUR_CODE);

        // get agency name (hard-code E&V or McGill for non-restricted offices) - only restricted show full agency name
        let agencyName = restricted
            ? ($ev_offices[_broker?.BUR_CODE!] ?? _firme?.NOM_LEGAL ?? "")
            : ($config.is_mcgill ? "McGill Immobilier" : "Engel & Völkers");

        // return final title-cased agency name
        return agencyName.trim()
            .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
            .replace("Volkers", "Völkers");
    }

    // full standard agency name w/o conditions or shortened version
    const agencyName = (broker?: Maybe<string>) => {
        const _broker = membreByCode(broker);
        const _office = bureauByCode(_broker?.BUR_CODE);
        const _firme = firmeByBureau(_broker?.BUR_CODE);

        // 2025-11-15: old method w/ custom office names, but now we switched back to official names from OACIQ
        // return $ev_offices[_broker?.BUR_CODE!] ?? `${_firme?.NOM_LEGAL} - ${_office?.NOM_LEGAL}`;

        // 2025-11-15: this is the official names of agency & office from OACIQ
        return `${_firme?.NOM_LEGAL} ⬩ ${_office?.NOM_LEGAL}`;
    }

    // whether specified office is from E&V
    const isEvOffice = (office: string) => {
        return Object.keys($ev_offices).includes(office);
    }

    // whether specified office if a McGill office
    const isMcGillOffice = (office: string) => {
        return office == "MCG001";
    }

    // given office code, return custom office name (if any)
    const customOfficeName = (office: string) => {
        return $ev_offices[office] ?? null;
    }

    /**
     * Updated 2024-08-22 by Patrice: consider a single listing 'restricted' IFF ALL its brokers are from restricted
     * offices; otherwise, if any of the brokers is from non-restricted office, the listing is considered to belong
     * to Patrice, and we show ONLY the non-restricted brokers (hiding the restricted ones), and we still index the
     * listing since the listing is co-listed by Patrice's own brokers.
     * This 'restriction' affects the 'index' / 'noindex' status of the entire listing.
     */
    const isRestricted = (listing: Maybe<CtrInscriptions>) => {
        // get all four offices for this listing
        const it = listing!;
        console.info(`restricted-offices`, $config.restricted_offices);

        // Note (2024-08-22): previously, a listing was 'restricted' if ANY broker was restricted
        // return arrayIntersect($config.restricted_offices ?? [], [
        //     it.BUREAU_INSCRIPTEUR_1,
        //     it.BUREAU_INSCRIPTEUR_2,
        //     it.BUREAU_INSCRIPTEUR_3,
        //     it.BUREAU_INSCRIPTEUR_4
        // ]).length > 0;

        // 2024-08-22: now, a listing is 'restricted' if ALL its brokers are either restricted or non-EV (e.g. Remax)
        // note: beware of 'null' in brokers #2,3,4 which are non-restricted because 'null' does not match any office
        return [it.BUREAU_INSCRIPTEUR_1, it.BUREAU_INSCRIPTEUR_2, it.BUREAU_INSCRIPTEUR_3, it.BUREAU_INSCRIPTEUR_4]
            .filter(it => it != null)
            .every(it => (!isEvOffice(it) && !isMcGillOffice(it))
                || $config.restricted_offices?.includes(it ?? null));
    }

    /**
     * Decides whether the current site may index a displayed listing.
     * Office restrictions always win; missing policy falls back to the restrictive broker-entitlement mode.
     */
    const isIndexable = (listing: Maybe<CtrInscriptions>) => {
        const it = listing!;
        if (isRestricted(it)) return false;

        const policy = $config.listing_index_policy ?? WebsiteListingIndexPolicy.BrokersOnly;
        if (policy === WebsiteListingIndexPolicy.DisplayedListings) return true;
        if (policy === WebsiteListingIndexPolicy.NoindexAll) return false;

        return [
            it.COURTIER_INSCRIPTEUR_1,
            it.COURTIER_INSCRIPTEUR_2,
            it.COURTIER_INSCRIPTEUR_3,
            it.COURTIER_INSCRIPTEUR_4
        ].some(it => $config.brokers?.includes(it ?? null));
    }

    useEffect(() => {
        if (empty($firmes)) loadFirmes();
        if (empty($bureaux)) loadBureaux();
        if (empty($membres)) loadMembres();
        if (empty($ev_offices)) loadEvOffices();
    }, [$firmes, $bureaux, $membres, $ev_offices]);

    return {
        $firmes,
        $bureaux,
        $membres,
        membreByCode,
        bureauByCode,
        firmeByBureau,
        agencyName,
        shortAgencyName,
        customOfficeName,
        isRestricted,
        isIndexable,
        isEvOffice,
        isMcGillOffice,
        loading: loading1 || loading2 || loading3
    }
}
